import React, { Dispatch, useEffect } from 'react';
import {
    Box,
    Dialog,
    Typography,
    SxProps,
    Tabs,
    Tab,
    IconButton,
    ListItem,
    Divider,
    ListItemText,
    ListItemIcon,
    Stack,
    Link,
} from '@mui/material';
import Icon from '@mdi/react';
import {
    mdiAccountBoxOutline,
    mdiCardAccountMailOutline,
    mdiDownload,
    mdiEye,
    mdiInformationOutline,
    mdiShieldCrown,
    mdiSitemap,
} from '@mdi/js';
import { headingFontBold, vars } from '../../../../Theme';
import { useDispatch, useSelector } from 'react-redux';
import { IAppReduxStore } from '../../../../redux/store';
import {
    IProjectDocumentationContactInfo,
    IProjectDocumentationItem,
    closeProjectDocumentationDialog,
    downloadProjectDocumentationItem,
    getProjectDocumentation,
    getProjectDocumentationContact,
} from '../../../../redux/project/sliceProjectDocumentation';
import DialogHeader from '../_parts/DialogHeader';
import { useParams } from 'react-router-dom';
import { getFileTypeOrDefault } from '../../../../util/FileUtils';
import { useTranslation } from 'react-i18next';
import { SkeletonLayout, SkeletonType } from '../_parts/SkeletonLayout';
import { encodeBase64String } from '../../../../util/StringUtils';

/** Styles for Dialog's component. */
const dialogSx: SxProps = {
    '& .MuiDialog-paper': {
        width: '80%',
        height: '60%',
        minWidth: '350px',
        overflow: 'hidden',
    },
};

/** Styles for Dialog's content. */
const dialogContentSx: SxProps = {
    flexGrow: 1,
    display: 'flex',
    overflow: 'hidden',
};

/** Styles for Dialog's tabs. */
const tabsSx: SxProps = {
    borderRight: 1,
    borderColor: vars.lightGray,
    padding: ' 0.5rem',
    '.MuiTabs-indicator': {
        display: 'none',
    },
    flex: '0 0 auto',
};

/** Styles for Dialog's tab. */
const tabSx: SxProps = {
    ...headingFontBold,
    width: '100%',
    textTransform: 'capitalize',
    justifyContent: 'flex-start',
    flexDirection: 'row',
    minHeight: 'auto',
    fontSize: 16,
    color: vars.font,
    p: 1.5,
    '&.Mui-selected': {
        color: vars.font,
        backgroundColor: vars.bgGreen,
    },
    '& .MuiTab-icon': {
        mb: 0,
        mr: 1,
        width: '24px',
        height: '24px',
        flex: '0 0 auto',
    },
};

const listItemTextSx: SxProps = { color: vars.font, ...headingFontBold, maxWidth: '85%' };

const ContactCard = ({ title, contact }: { title: string; contact: string }) => {
    const { t } = useTranslation('translations', { keyPrefix: 'projectDocumentationDialog' });
    return (
        <Stack spacing={1}>
            <Typography
                sx={{
                    ...headingFontBold,
                    fontSize: 16,
                    color: vars.font,
                }}
            >
                {t(title)}
            </Typography>
            <Stack direction='row' sx={{ display: 'flex', alignItems: 'flex-start' }} spacing={1}>
                <Icon path={mdiCardAccountMailOutline} size={1.0} />
                <Typography sx={{ whiteSpaceCollapse: 'break-spaces' }}>{contact}</Typography>
            </Stack>
        </Stack>
    );
};

const ContactCard2 = ({ title, contact }: { title: string; contact: IProjectDocumentationContactInfo }) => {
    const { t } = useTranslation('translations', { keyPrefix: 'projectDocumentationDialog' });
    return (
        <Stack spacing={1}>
            <Typography
                sx={{
                    ...headingFontBold,
                    fontSize: 16,
                    color: vars.font,
                }}
            >
                {t(title)}
            </Typography>
            <Stack direction='row' sx={{ display: 'flex', alignItems: 'flex-start' }} spacing={1}>
                <Icon path={mdiCardAccountMailOutline} size={1.0} />
                <Stack>
                    <Typography sx={{ whiteSpaceCollapse: 'break-spaces' }}>Support {contact.supportNumber ? `\r\n${contact.supportNumber}`: ''}</Typography>
                    {!contact.supportSystem || contact.supportSystem === 'eplass' && <Typography sx={{ whiteSpaceCollapse: 'break-spaces' }}>{contact.supportMail}</Typography>}
                    {contact.supportSystem === 'thinkproject' && <Link href={contact.supportURL} underline='hover' target='_blank'
                        sx={{
                            color: 'inherit',
                            wordWrap: 'break-word',
                            wordBreak: 'break-all',
                        }}
                    >{contact.supportURL}</Link>}
                </Stack>
            </Stack>
        </Stack>
    );
};

/**
 * TabPanel Component for each tab.
 */
const TabPanel = ({ data, contactData, title, value, index, ...other }: ITabPanelProps) => {
    /** Configure component translation. */
    const { t } = useTranslation('translations', { keyPrefix: 'projectDocumentationDialog' });
    const dispatch: Dispatch<unknown> = useDispatch();
    const { replicaId } = useParams();

    const getFileIcon = (fileName: string): { path: string; color: string } => {
        const fileType = getFileTypeOrDefault(fileName);
        return {
            path: fileType.icon,
            color: fileType.color,
        };
    };

    return (
        <>
            {value === index && (
                <Box
                    role='tabpanel'
                    hidden={value !== index}
                    id={`vertical-tabpanel-${index}`}
                    aria-labelledby={`vertical-tab-${index}`}
                    sx={{ display: 'flex', flexGrow: 1 }}
                    {...other}
                >
                    <Stack
                        direction={'column'}
                        sx={{
                            p: '0.5rem',
                            flexGrow: 1,
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                            }}
                        >
                            <Typography
                                variant='h6'
                                sx={{
                                    ...headingFontBold,
                                    fontSize: 18,
                                    color: vars.font,
                                    textTransform: 'capitalize',
                                }}
                            >
                                {t(title)}
                            </Typography>
                        </Box>
                        <Divider />
                        {title !== 'contacts' ? (
                            <Box sx={{ overflow: 'auto' }}>
                                {data.map((d, index) => (
                                    <ListItem
                                        key={d.docUNID}
                                        sx={{
                                            pl: 1,
                                            backgroundColor:
                                                index % 2 ? vars.lightGray : vars.white,
                                        }}
                                        secondaryAction={
                                            <>
                                                {d.viewable && (
                                                    <IconButton
                                                        edge='end'
                                                        onClick={() =>
                                                            window.open(
                                                                `/project-documentation/${replicaId}/${
                                                                    d.docUNID
                                                                }/${encodeBase64String(d.files[0])}`,
                                                            )
                                                        }
                                                    >
                                                        <Icon
                                                            path={mdiEye}
                                                            color={vars.font}
                                                            size={1}
                                                        />
                                                    </IconButton>
                                                )}
                                                <IconButton
                                                    edge='end'
                                                    onClick={() =>
                                                        dispatch(
                                                            downloadProjectDocumentationItem({
                                                                replicaId: replicaId,
                                                                docUnId: d.docUNID,
                                                                fileName: d.files[0],
                                                            }),
                                                        )
                                                    }
                                                >
                                                    <Icon
                                                        path={mdiDownload}
                                                        color={vars.font}
                                                        size={1}
                                                    />
                                                </IconButton>
                                            </>
                                        }
                                    >
                                        <ListItemIcon sx={{ minWidth: '24px', pr: 1 }}>
                                            <Icon
                                                path={getFileIcon(d.files[0])?.path}
                                                color={getFileIcon(d.files[0])?.color}
                                                size={1.0}
                                            />
                                        </ListItemIcon>
                                        <ListItemText primary={d.subject} sx={listItemTextSx} />
                                    </ListItem>
                                ))}
                            </Box>
                        ) : (
                            <Stack
                                spacing={1}
                                sx={{
                                    paddingTop: 1,
                                    overflow: 'auto',
                                }}
                            >
                                <ContactCard title='contactProject' contact={contactData.contact} />
                                {(contactData.supportMail || contactData.supportNumber) && (
                                    <ContactCard2 title='contactCDEInfrastructure' contact={contactData} />
                                )}
                            </Stack>
                        )}
                    </Stack>
                </Box>
            )}
        </>
    );
};

const projectDocumentationTabsConfig = {
    Admin: {
        name: 'administration',
        icon: mdiShieldCrown,
    },
    Workflow: {
        name: 'workflows',
        icon: mdiSitemap,
    },
    General: {
        name: 'general',
        icon: mdiInformationOutline,
    },
    Contacts: {
        name: 'contacts',
        icon: mdiAccountBoxOutline,
    },
};

/**
 * Dialog Component that provides project documentation.
 */
export default () => {
    /** State slice that uses to switch tabs. */
    const [value, setValue] = React.useState(0);

    const { t } = useTranslation('translations', { keyPrefix: 'projectDocumentationDialog' });
    const dispatch: Dispatch<unknown> = useDispatch();

    const { replicaId } = useParams();

    const {
        isDialogOpen,
        isLoadingData,
        projectDocumentationData,
        projectDocumentationContactData,
    } = useSelector((_store: IAppReduxStore) => _store.projectDocumentation);

    useEffect(() => {
        if (isDialogOpen && replicaId) {
            dispatch(getProjectDocumentation({ replicaId }));
            dispatch(getProjectDocumentationContact({ replicaId }));
        }
    }, [replicaId, isDialogOpen]);

    /**
     * Switches tabs.
     * @param event
     * @param newValue New Tab's value.
     */
    const handleChange = (event: React.SyntheticEvent, newValue: number) => {
        setValue(newValue);
    };

    const handleClose = () => {
        dispatch(closeProjectDocumentationDialog());
    };

    return (
        <Dialog open={isDialogOpen} sx={dialogSx}>
            <DialogHeader onClose={handleClose} dialogTitle={t('projectDocumentation')} />
            <Divider />
            <SkeletonLayout loading={isLoadingData} type={SkeletonType.Documentation}>
                <Box sx={dialogContentSx}>
                    <Tabs
                        orientation='vertical'
                        variant='scrollable'
                        value={value}
                        onChange={handleChange}
                        sx={tabsSx}
                    >
                        {projectDocumentationData &&
                            Object.keys(projectDocumentationData).map(tab => (
                                <Tab
                                    key={tab}
                                    icon={
                                        <Icon
                                            path={projectDocumentationTabsConfig[tab].icon}
                                            size={1}
                                        />
                                    }
                                    label={t(projectDocumentationTabsConfig[tab].name)}
                                    id={`vertical-tab-${tab}`}
                                    aria-controls={`vertical-tabpanel-${tab}`}
                                    sx={tabSx}
                                />
                            ))}
                        {projectDocumentationContactData && (
                            <Tab
                                key={'Contacts'}
                                icon={
                                    <Icon
                                        path={projectDocumentationTabsConfig['Contacts'].icon}
                                        size={1}
                                    />
                                }
                                label={t(projectDocumentationTabsConfig['Contacts'].name)}
                                id={`vertical-tab-${'Contacts'}`}
                                aria-controls={`vertical-tabpanel-${'Contacts'}`}
                                sx={tabSx}
                            />
                        )}
                    </Tabs>
                    {projectDocumentationData &&
                        Object.keys(projectDocumentationData).map((tab, index) => (
                            <TabPanel
                                key={tab}
                                data={projectDocumentationData[tab]}
                                title={projectDocumentationTabsConfig[tab].name}
                                value={value}
                                index={index}
                                onClose={handleClose}
                            />
                        ))}
                    {projectDocumentationContactData && (
                        <TabPanel
                            key={'Contacts'}
                            contactData={projectDocumentationContactData}
                            title={projectDocumentationTabsConfig['Contacts'].name}
                            value={value}
                            index={
                                projectDocumentationData
                                    ? Object.keys(projectDocumentationData).length
                                    : 0
                            }
                            onClose={handleClose}
                        />
                    )}
                </Box>
            </SkeletonLayout>
        </Dialog>
    );
};

/**
 * Interface that describes TabPanel's props.
 */
interface ITabPanelProps {
    title: string;
    data?: IProjectDocumentationItem[];
    contactData?: IProjectDocumentationContactInfo;
    index: number;
    value: number;
    onClose: () => void;
}
